/*
 * Copyright 2026 Sony Corporation
 */

import type { DeviceStateResponse } from '@ips-device-verse/twin-service/schema';
import type { RealtimeEvent } from '@ips-device-verse/twin-service/schema/realtime';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { createRealtimeClient, type RealtimeClient, type RealtimeStatus } from '../services/realtime-client';

export interface UseRealtimeOptions {
  worldId: string;
  /** Another tab, or another user, bound the world to a different scene. */
  onSceneChanged: (sceneId: string | null) => void;
}

export interface UseRealtimeResult {
  status: RealtimeStatus;
  /** The latest shadow the service pushed, per device. */
  states: Record<string, DeviceStateResponse>;
  /** Whether each gateway's own link to the cloud is up, as it last said. */
  gateways: Record<string, 'online' | 'offline'>;
  /**
   * The socket itself, for the agent.
   *
   * One socket per world, not one per feature: the turn is a reply on the
   * connection `$connect` already authorized, and a second one would be a
   * second authorization, a second reconnect loop and a second row in the table.
   */
  socket: Pick<RealtimeClient, 'send'>;
  subscribe: (listener: (event: RealtimeEvent) => void) => () => void;
}

interface Live {
  /** Stamped so opening another world discards the old one by derivation. */
  worldId: string | undefined;
  states: Record<string, DeviceStateResponse>;
  versions: Record<string, number>;
  gateways: Record<string, 'online' | 'offline'>;
}

const EMPTY: Live = { worldId: undefined, states: {}, versions: {}, gateways: {} };

/**
 * Keeps this tab in step with everyone else's writes.
 *
 * Updates are applied by version, not by arrival. A shadow write produces a
 * message per watching socket and the network does not promise an order, so an
 * older document can land after a newer one; without the check a device would
 * briefly jump back to a value nobody holds any more.
 */
export const useRealtime = ({ worldId, onSceneChanged }: UseRealtimeOptions): UseRealtimeResult => {
  const { config, auth } = useAuth();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const [live, setLive] = useState<Live>(EMPTY);
  const active = live.worldId === worldId ? live : EMPTY;

  // The socket is opened once per world; these change with every render and
  // must not be a reason to tear it down and reconnect.
  const onSceneChangedRef = useRef(onSceneChanged);
  useEffect(() => {
    onSceneChangedRef.current = onSceneChanged;
  });

  const clientRef = useRef<RealtimeClient>(null);
  const listeners = useRef(new Set<(event: RealtimeEvent) => void>());

  const subscribe = useCallback((listener: (event: RealtimeEvent) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const send = useCallback(
    (frame: Parameters<RealtimeClient['send']>[0]) => clientRef.current?.send(frame) ?? false,
    []
  );
  const socket = useMemo(() => ({ send }), [send]);

  useEffect(() => {
    const client = createRealtimeClient({
      url: config.ws_url,
      worldId,
      getIdToken: auth.getIdToken,
      onStatus: setStatus,
      onEvent: (event: RealtimeEvent) => {
        for (const listener of listeners.current) {
          listener(event);
        }

        if (event.type === 'scene_changed') {
          onSceneChangedRef.current(event.scene_id);
          return;
        }

        if (event.type === 'gateway_status') {
          setLive((previous) => {
            const base = previous.worldId === worldId ? previous : EMPTY;
            return { ...base, worldId, gateways: { ...base.gateways, [event.gateway_id]: event.status } };
          });
          return;
        }

        // The agent answers on the same socket, to the tab that asked; this
        // hook is about the world, and a turn belongs to whoever started it.
        if (event.type !== 'device_state') {
          return;
        }

        setLive((previous) => {
          const base = previous.worldId === worldId ? previous : EMPTY;
          const seen = base.versions[event.device_id];
          if (event.version !== undefined && seen !== undefined && event.version <= seen) {
            return previous;
          }
          return {
            worldId,
            states: {
              ...base.states,
              [event.device_id]: {
                device_id: event.device_id,
                thing_name: '',
                state: event.state,
                version: event.version,
                reported_at: event.reported_at,
              },
            },
            versions: { ...base.versions, [event.device_id]: event.version ?? 0 },
            gateways: base.gateways,
          };
        });
      },
    });

    clientRef.current = client;
    return () => {
      clientRef.current = null;
      client.close();
    };
  }, [config.ws_url, worldId, auth]);

  return { status, states: active.states, gateways: active.gateways, socket, subscribe };
};
